import type { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import type {
  BasicReference,
  Page as CMSPage,
  ContentItem,
  Website
} from '@ps/web-martech-cms-types';
import type { SportsContentBlock, SportsContentItem } from '@ps/web-sports-page-types';

import type { Model } from '../../types';

import effects from './effects';
import selectors from './selectors';

type Optional<T, K extends keyof T> = Omit<T, K> & Partial<T>;

type SportsAlias = `sports${string}`;
type CMSAlias = CMSPage['alias'];

interface SportsLayout extends Omit<CMSPage['layout'], 'layout_type'> {
  layout_type: 'SportsLayout' | 'SportsBasicLayout' | 'SportsSelfExcludedLayout';
}

type SportsPage = Omit<CMSPage, 'alias' | 'layout' | 'content'> & {
  alias?: SportsAlias;
  layout: SportsLayout;
  content: SportsContentBlock[];
};

export type Page = CMSPage | SportsPage;
export type Alias = NonNullable<CMSAlias> | SportsAlias;

export interface BasicContentItem extends BasicReference {
  title: string;
}

export type CMSState = Optional<WebsiteResponse, 'website' | 'aliases'> & {
  pathBrand?: BrandTypes;
};

export interface WebsiteResponse {
  website: Website;
  content: Record<string, ContentItem | SportsContentItem>;
  aliases: Partial<{ [key in Alias]: string }>;
}

export interface RoutesResponse {
  [key: string]: string;
}

interface CMSModel extends Model<CMSState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

export const defaultState = { content: {} };

const reducers = {
  setWebsite: (state: CMSState, payload: Optional<WebsiteResponse, 'website' | 'aliases'>) => ({
    ...state,
    ...payload
  }),
  addAliases: (state: CMSState, aliases: CMSState['aliases']) => ({
    ...state,
    aliases: {
      ...state.aliases,
      ...aliases
    }
  })
};

const page: CMSModel = {
  state: defaultState,
  reducers,
  effects,
  selectors
};

export default page;
